import { useGetAuthSessionQuery } from '@sorrem-client/ui/store';
import { getStoredSessionToken, getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';

export type AuthSessionCheckStatus = 'checking' | 'valid' | 'invalid';

function hasStoredSession(): boolean {
    return getStoredCanonicalAccountId() !== null && getStoredSessionToken() !== null;
}

/**
 * Confirms the stored session token with the server (GET /api/auth/session) when
 * the page loads, so a reload with an expired or revoked token is caught
 * before any signed-in screen renders. It shares the RTK query that
 * developer access reads, so the page asks once. A 401 expires the session
 * (see the store's base query). Any other failure, such as an unreachable
 * server, counts as valid: it doesn't prove the token is bad, and the
 * screen's own requests will surface the error.
 */
export function useAuthSessionCheck(): AuthSessionCheckStatus {
    const hasSession = hasStoredSession();
    const { isLoading, error } = useGetAuthSessionQuery(undefined, { skip: !hasSession });

    if (!hasSession) {
        return 'invalid';
    }
    if (isLoading) {
        return 'checking';
    }
    return error !== undefined && 'status' in error && error.status === 401 ? 'invalid' : 'valid';
}
